"use client"

import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { Card, Badge, Spinner, Alert } from "react-bootstrap"
import { Bell, ChevronUp, Image as ImageIcon, MoreHorizontal } from "lucide-react"
import type { Announcement } from "../../data/announcements"
import { type Building, compareBuildingNames } from "../../data/buildings"
import { useTranslation } from "../../hooks/use-translation"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { fetchAnnouncements } from "../../redux/slices/announcementsSlice"
import { fetchBuildings } from "../../redux/slices/buildingsSlice"
import { useAuth } from "../../contexts/auth-context"
import { PlusButton } from "../../components/ui/plus-button"

export default function AnnouncementsPage() {
  const dispatch = useAppDispatch()
  const { user } = useAuth()
  const { announcements = [], isLoading, error } = useAppSelector((state) => state.announcements)
  const { buildings = [] } = useAppSelector((state) => state.buildings)
  const { t } = useTranslation()

  // Fetch announcements when component mounts
  useEffect(() => {
    dispatch(fetchAnnouncements())
    dispatch(fetchBuildings(false)) // Only fetch active buildings
  }, [dispatch])

  const isAdmin = user?.role === "admin"

  // Filter announcements for the user's building
  const userBuilding = user?.building || ""
  const filteredAnnouncements = announcements
    ? announcements.filter(
        (announcement) =>
          announcement.buildings &&
          (announcement.buildings.includes(userBuilding) || announcement.buildings.includes("all")),
      )
    : []

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" style={{ color: "white" }} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="py-3">
        <Alert variant="danger">{error}</Alert>
      </div>
    )
  }

  return (
    <div className="py-4 px-2 px-md-4">
      <div className="d-flex justify-content-between align-items-center mb-4" style={{ height: "64px" }}>
        <div className="d-flex align-items-center gap-3">
          <h1 className="fs-2 fw-bold mb-0 text-white">{t("announcements")}</h1>
          <Bell className="text-white" size={32} />
        </div>
        {isAdmin && <PlusButton title={t("manageAnnouncements")} to="/dashboard/admin/announcements" />}
      </div>

      {filteredAnnouncements.length === 0 ? (
        <div className="d-flex justify-content-center align-items-center border border-dashed rounded p-5">
          <p className="text-white mb-0">{t("noAnnouncements")}</p>
        </div>
      ) : (
        <div className="d-flex flex-column gap-4">
          {filteredAnnouncements.map((announcement) => (
            <AnnouncementCard key={announcement.id} announcement={announcement} buildings={buildings} />
          ))}
        </div>
      )}
    </div>
  )
}

const COLLAPSED_LINES = 3

function AnnouncementCard({ announcement, buildings }: { announcement: Announcement; buildings: Building[] }) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(announcement.showExpanded === true)
  const [overflowsCollapsed, setOverflowsCollapsed] = useState(false)
  const contentRef = useRef<HTMLParagraphElement>(null)
  const contentId = `announcement-content-${announcement.id}`
  const buildingNameOf = (buildingId: string) => buildings.find((b) => b.id === buildingId)?.name || buildingId

  // scrollHeight is the full text height even while line-clamped, so this works in both states
  useLayoutEffect(() => {
    const el = contentRef.current
    if (!el) return

    const measure = () => {
      const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight)
      if (Number.isNaN(lineHeight)) {
        setOverflowsCollapsed(true)
        return
      }
      setOverflowsCollapsed(el.scrollHeight > lineHeight * COLLAPSED_LINES + 1)
    }

    measure()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [announcement.content])

  const canToggle = overflowsCollapsed || !!announcement.imageUrl

  return (
    <Card
      className="border-0 shadow-sm"
      style={{
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        borderRadius: "20px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
      }}
    >
      <Card.Body className="p-4">
        <div className="d-flex align-items-start gap-3">
          <div className="flex-grow-1">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <h5 className="text-white mb-0 fw-bold">{announcement.title}</h5>
              <div className="text-end">
                <small className="text-white opacity-75 d-block">
                  {new Date(announcement.date).toLocaleTimeString("el-GR", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </small>
                <small className="text-white opacity-75">
                  {new Date(announcement.date).toLocaleDateString("el-GR", {
                    month: "numeric",
                    day: "numeric",
                    year: "numeric",
                  })}
                </small>
              </div>
            </div>
            <div id={contentId}>
              <p
                ref={contentRef}
                className="text-white mb-3 opacity-90"
                style={
                  isExpanded
                    ? { whiteSpace: "pre-line" }
                    : {
                        whiteSpace: "pre-line",
                        display: "-webkit-box",
                        WebkitLineClamp: COLLAPSED_LINES,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }
                }
              >
                {announcement.content}
              </p>
              {isExpanded && announcement.imageUrl && (
                <a href={announcement.imageUrl} target="_blank" rel="noopener noreferrer" className="d-block mb-3">
                  <img
                    src={announcement.imageUrl}
                    alt={announcement.title}
                    style={{
                      width: "100%",
                      maxWidth: "800px",
                      objectFit: "contain",
                      borderRadius: "12px",
                    }}
                  />
                </a>
              )}
            </div>
            <div className="d-flex align-items-center justify-content-between gap-2">
              <div className="d-flex flex-wrap gap-2">
                {announcement.buildings &&
                  [...announcement.buildings]
                    .sort((a, b) => compareBuildingNames(buildingNameOf(a), buildingNameOf(b)))
                    .map((building) => {
                      if (building === "all") {
                        return (
                          <Badge
                            key={building}
                            className="py-2 px-3 rounded-pill"
                            style={{
                              backgroundColor: "rgba(255, 255, 255, 0.2)",
                              color: "white",
                              border: "1px solid rgba(255, 255, 255, 0.3)",
                            }}
                          >
                            {t("allBuildings")}
                          </Badge>
                        )
                      }

                      const buildingName = buildingNameOf(building)
                      return (
                        <Badge
                          key={building}
                          className="py-2 px-3 rounded-pill"
                          style={{
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                            color: "white",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                          }}
                        >
                          {buildingName}
                        </Badge>
                      )
                    })}
              </div>
              {canToggle && (
                <button
                  type="button"
                  className="announcement-toggle-btn btn btn-sm text-white d-inline-flex align-items-center gap-1 flex-shrink-0"
                  aria-expanded={isExpanded}
                  aria-controls={contentId}
                  aria-label={isExpanded ? t("collapseAnnouncement") : t("expandAnnouncement")}
                  onClick={() => setIsExpanded((prev) => !prev)}
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp size={16} aria-hidden="true" />
                      <span>{t("showLess")}</span>
                    </>
                  ) : (
                    <>
                      {/* A collapsed card hides its image — without this hint a one-line post shows ⋯ for no visible reason */}
                      {announcement.imageUrl && <ImageIcon size={16} aria-hidden="true" />}
                      <MoreHorizontal size={20} aria-hidden="true" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
